import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import { AvatarWithInfo } from 'components/reusables/avatarWithInfo/AvatarWithInfo'
import ProfilesCountCell from 'components/reusables/profilesCountCell'
import { IMainState } from 'store/states'
import { usePermissionsList } from 'hooks/usePermissionsList'
import styles from './styles.module.scss'

const prefixUserList = 'configuration.users.list'
const translateHeaderPrefix = `${prefixUserList}.headers`

export const PARTICIPANTS_CHECKBOX_WIDTH = 64

const NAME_COLUMN_WIDTH = 260
const POSITION_COLUMN_WIDTH = 220
const IDENTIFIER_COLUMN_WIDTH = 220
const AREA_COLUMN_WIDTH = 200
const ROLE_COLUMN_WIDTH = 180
const PROFILES_COLUMN_WIDTH = 130
const MANAGER_COLUMN_WIDTH = 240

export const PARTICIPANTS_TABLE_SCROLL_X =
  PARTICIPANTS_CHECKBOX_WIDTH +
  NAME_COLUMN_WIDTH +
  POSITION_COLUMN_WIDTH +
  IDENTIFIER_COLUMN_WIDTH +
  AREA_COLUMN_WIDTH +
  ROLE_COLUMN_WIDTH +
  PROFILES_COLUMN_WIDTH +
  MANAGER_COLUMN_WIDTH

export const useColumns = () => {
  const { t } = useTranslation()

  const lmsProfileList = useSelector(
    ({ lmsProfiles }: IMainState) => lmsProfiles.allProfilesList
  )

  const permissionsList = usePermissionsList()

  const columns = useMemo(
    () => [
      {
        title: t(`${translateHeaderPrefix}.name`),
        key: 'full_name',
        width: NAME_COLUMN_WIDTH,
        fixed: 'left',
        className: styles.nameColumn,
        render: ({ fullName, avatar, id }: any) => (
          <Link
            to={`/configuration/user-profile/${id}`}
            className={styles.profileLink}
          >
            <AvatarWithInfo
              borderRadius={50}
              icon={faUser}
              image={avatar}
              principalColor="#0085FF"
              background="#0085FF"
              size={30}
              title={fullName || ''}
              fontWeight="normal"
            />
          </Link>
        ),
        sorteable: true,
      },
      {
        title: t(`${translateHeaderPrefix}.identifier`),
        key: 'identifier',
        width: IDENTIFIER_COLUMN_WIDTH,
        className: styles.identifierColumn,
        sorteable: true,
        render: (identifier: string) => (
          <span className={styles.cellEllipsis} title={identifier}>
            {identifier}
          </span>
        ),
      },
      {
        title: t(`${translateHeaderPrefix}.position`),
        key: 'position',
        width: POSITION_COLUMN_WIDTH,
        className: styles.positionColumn,
        visible: true,
        render: (position: string) => (
          <span className={styles.cellEllipsis} title={position}>
            {position}
          </span>
        ),
      },
      {
        title: t(`${translateHeaderPrefix}.area`),
        key: 'area_name',
        width: AREA_COLUMN_WIDTH,
        className: styles.areaColumn,
        render: (areaName: string) => (
          <span className={styles.cellEllipsis} title={areaName}>
            {areaName}
          </span>
        ),
      },
      {
        title: t(`${translateHeaderPrefix}.abacRole`, {
          defaultValue: t(`${translateHeaderPrefix}.roleAssigned`, {
            defaultValue: 'Rol',
          }),
        }),
        key: 'abac_role_name',
        width: ROLE_COLUMN_WIDTH,
        className: styles.roleColumn,
        render: (_: any, row: any) => {
          const role = row.abacRole || row.role
          if (!role?.id && !role?.name) return ''
          return (
            <p className={styles.roleCell}>
              {role.name}
              {role.code ? (
                <>
                  <br />
                  <span className={styles.greyCode}>({role.code})</span>
                </>
              ) : null}
            </p>
          )
        },
        sorteable: true,
        filterable: false,
      },
      {
        title: t(`${translateHeaderPrefix}.lmsProfile`),
        key: 'lms_profiles_id',
        width: PROFILES_COLUMN_WIDTH,
        className: styles.profilesColumn,
        filterable: true,
        type: 'list',
        list: lmsProfileList
          .map(({ id, name, color }) => ({ value: id, label: name, color }))
          .sort((A, B) => A.label.localeCompare(B.label)),
        render: (_: any, row: any) => (
          <div className={styles.profileCell}>
            <ProfilesCountCell
              resourceId={row.id}
              resourceType="user"
              count={row.lmsProfiles?.length || 0}
            />
          </div>
        ),
      },
      {
        title: t(`${translateHeaderPrefix}.role.title`),
        key: 'abac_default_admin',
        visible: false,
        type: 'list',
        list: permissionsList,
      },
      {
        title: t(`${translateHeaderPrefix}.directManager`, {
          defaultValue: 'Jefe directo',
        }),
        key: 'direct_manager',
        width: MANAGER_COLUMN_WIDTH,
        className: styles.managerColumn,
        filterable: false,

        render: (managers: any) => (
          <>
            {(managers || []).map((manager, index) => {
              const fullName =
                manager?.fullName ||
                [manager?.name, manager?.lastname].filter(Boolean).join(' ') ||
                ''

              return (
                <AvatarWithInfo
                  key={index}
                  borderRadius={50}
                  icon={faUser}
                  image={manager?.avatar}
                  size={30}
                  title={fullName}
                  subtitle={manager?.position || undefined}
                  fontWeight="normal"
                />
              )
            })}
          </>
        ),
        sorteable: true,
      },
    ],
    [lmsProfileList, permissionsList, t]
  )

  return columns
}
